import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  MdArrowBack, 
  MdCheckCircle, 
  MdBuild, 
  MdTimer, 
  MdCheckBox, 
  MdCheckBoxOutlineBlank, 
  MdFilterList, 
  MdArrowUpward, 
  MdArrowDownward,
  MdInventory,
  MdCategory,
  MdStorage,
  MdLocalShipping,
  MdInfo,
  MdWarning
} from "react-icons/md";

const ProductDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const product = location.state?.product;
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [ageRange, setAgeRange] = useState({ min: "", max: "" });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const itemsPerPage = 10;

  // All hooks must be here, before any return
  const filteredProducts = useMemo(() => {
    if (!product) return [];
    
    let sortedProducts = [...product.individualProducts];
    
    if (sortConfig.key) {
      sortedProducts.sort((a, b) => {
        if (sortConfig.key === 'status') {
          const aStatus = a.needsRework ? 'rework' : 'ready';
          const bStatus = b.needsRework ? 'rework' : 'ready';
          if (aStatus < bStatus) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (aStatus > bStatus) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        }
        
        if (sortConfig.key === 'age') {
          if (a.age < b.age) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
          }
          if (a.age > b.age) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
          }
          return 0;
        }
        
        return 0;
      });
    }
    
    return sortedProducts;
  }, [product, sortConfig]);

  // Calculate pagination values
  const totalItems = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = Math.min((currentPage - 1) * itemsPerPage, totalItems);
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentItems = filteredProducts.slice(startIndex, endIndex);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, ageRange, sortConfig]);

  // Now, after all hooks, do the early return
  if (!product) {
    return (
      <div className="mt-5 p-4">
        <div className="flex items-center space-x-2 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
          >
            <MdArrowBack size={20} />
            <span>Back to Dashboard</span>
          </button>
        </div>
        <div className="text-center text-gray-500">Product not found</div>
      </div>
    );
  }

  // Handle row selection
  const handleSelectRow = (index) => {
    const actualIndex = startIndex + index;
    setSelectedRows(prev => {
      if (prev.includes(actualIndex)) {
        return prev.filter(i => i !== actualIndex);
      }
      return [...prev, actualIndex];
    });
  };

  // Handle select all rows
  const handleSelectAll = () => {
    if (selectedRows.length === currentItems.length) {
      setSelectedRows([]);
    } else {
      const newSelectedRows = currentItems.map((_, index) => startIndex + index);
      setSelectedRows(newSelectedRows);
    }
  };

  // Handle schedule rework for selected rows
  const handleScheduleRework = () => {
    // TODO: Implement rework scheduling logic
    console.log('Scheduling rework for:', selectedRows);
  };

  // Handle filter changes
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleAgeFilterChange = (e) => {
    const { name, value } = e.target;
    setAgeRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle sorting
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <MdFilterList className="text-gray-400" size={16} />;
    }
    return sortConfig.direction === 'ascending' ? 
      <MdArrowUpward className="text-blue-500" size={16} /> : 
      <MdArrowDownward className="text-blue-500" size={16} />;
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);
      
      // Calculate start and end of visible pages
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if at the start
      if (currentPage <= 2) {
        endPage = 4;
      }
      // Adjust if at the end
      if (currentPage >= totalPages - 1) {
        startPage = totalPages - 3;
      }
      
      // Add ellipsis if needed
      if (startPage > 2) {
        pageNumbers.push('...');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      
      // Always show last page
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  return (
    <div className="mt-5 p-4">
      {/* Header with Back Button and 3D Effect */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transform hover:scale-105 transition-all duration-200"
          >
            <div className="bg-blue-50 p-2 rounded-lg transform rotate-3 shadow-md">
              <MdArrowBack size={24} />
            </div>
            <span className="font-medium">Back to Dashboard</span>
          </button>
        </div>
        <div className="flex items-center space-x-2">
          <div className="bg-blue-50 p-2 rounded-lg transform -rotate-3 shadow-md">
            <MdInfo className="text-blue-600" size={24} />
          </div>
        </div>
      </div>

      {/* Page Header with 3D Effect */}
      <div className="mb-8 relative">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-lg transform perspective-1000">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-4 rounded-lg transform rotate-3 shadow-lg">
                <MdInventory className="text-white" size={32} />
              </div>
              <div>
                <div className="flex items-center space-x-3">
                  <h1 className="text-3xl font-bold text-gray-800 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                    {product.name}
                  </h1>
                  <div className="bg-gradient-to-br from-purple-500 to-purple-700 px-4 py-1 rounded-full transform -rotate-3 shadow-md">
                    <span className="text-white font-semibold">W2 450</span>
                  </div>
                </div>
                <div className="mt-2 flex items-center space-x-2">
                  <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                    {product.quantity}
                  </span>
                  <span className="text-sm text-gray-600">boxes in stock</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards with 3D Effect */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg transform hover:scale-105 transition-all duration-300 shadow-lg">
          <div className="flex items-center space-x-3">
            <div className="bg-green-500 p-3 rounded-lg transform -rotate-6 shadow-md">
              <MdCheckCircle className="text-white" size={24} />
            </div>
            <div>
              <span className="text-sm font-medium text-green-700">Ready Units</span>
              <p className="text-2xl font-bold text-green-600 mt-1">
                {product.individualProducts.filter(p => !p.needsRework).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-lg transform hover:scale-105 transition-all duration-300 shadow-lg">
          <div className="flex items-center space-x-3">
            <div className="bg-orange-500 p-3 rounded-lg transform rotate-6 shadow-md">
              <MdBuild className="text-white" size={24} />
            </div>
            <div>
              <span className="text-sm font-medium text-orange-700">Needs Rework</span>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {product.individualProducts.filter(p => p.needsRework).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg transform hover:scale-105 transition-all duration-300 shadow-lg">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500 p-3 rounded-lg transform -rotate-6 shadow-md">
              <MdTimer className="text-white" size={24} />
            </div>
            <div>
              <span className="text-sm font-medium text-blue-700">Average Age</span>
              <p className="text-2xl font-bold text-blue-600 mt-1">
                {Math.round(product.individualProducts.reduce((sum, p) => sum + p.age, 0) / product.quantity)} days
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters with 3D Effect */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-md transform hover:scale-102 transition-all duration-200">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-50 p-2 rounded-lg">
              <MdFilterList className="text-blue-600" size={20} />
            </div>
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
            >
              <option value="all">All Status</option>
              <option value="ready">Ready</option>
              <option value="rework">Needs Rework</option>
            </select>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md transform hover:scale-102 transition-all duration-200">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-50 p-2 rounded-lg">
              <MdTimer className="text-blue-600" size={20} />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                name="min"
                value={ageRange.min}
                onChange={handleAgeFilterChange}
                placeholder="Min Age"
                className="block w-24 pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              />
              <span className="text-gray-500">to</span>
              <input
                type="number"
                name="max"
                value={ageRange.max}
                onChange={handleAgeFilterChange}
                placeholder="Max Age"
                className="block w-24 pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions with 3D Effect */}
      {selectedRows.length > 0 && (
        <div className="mb-6 bg-gradient-to-r from-orange-50 to-orange-100 p-4 rounded-lg shadow-md transform hover:scale-102 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-orange-500 p-2 rounded-lg transform rotate-3 shadow-md">
                <MdWarning className="text-white" size={20} />
              </div>
              <span className="text-sm font-medium text-orange-700">
                {selectedRows.length} {selectedRows.length === 1 ? 'item' : 'items'} selected
              </span>
            </div>
            <button
              onClick={handleScheduleRework}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors transform hover:scale-105 duration-200"
            >
              Schedule Rework for Selected
            </button>
          </div>
        </div>
      )}

      {/* Units Table with 3D Effect */}
      <div className="bg-white rounded-lg border shadow-lg transform hover:scale-102 transition-all duration-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center">
                    <button
                      onClick={handleSelectAll}
                      className="mr-2 text-gray-400 hover:text-gray-600 transform hover:scale-110 transition-all duration-200"
                    >
                      {selectedRows.length === currentItems.length ? (
                        <MdCheckBox size={20} />
                      ) : (
                        <MdCheckBoxOutlineBlank size={20} />
                      )}
                    </button>
                    Select
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <MdCategory className="text-gray-400" size={16} />
                    <span>Unit ID</span>
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => requestSort('age')}
                >
                  <div className="flex items-center space-x-2">
                    <MdTimer className="text-gray-400" size={16} />
                    <span>Age (days)</span>
                    {getSortIcon('age')}
                  </div>
                </th>
                <th 
                  className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => requestSort('status')}
                >
                  <div className="flex items-center space-x-2">
                    <MdCheckCircle className="text-gray-400" size={16} />
                    <span>Status</span>
                    {getSortIcon('status')}
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <MdBuild className="text-gray-400" size={16} />
                    <span>Actions</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((unit, index) => (
                <tr key={index} className="hover:bg-gray-50 transform hover:scale-102 transition-all duration-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleSelectRow(index)}
                      className="text-gray-400 hover:text-gray-600 transform hover:scale-110 transition-all duration-200"
                    >
                      {selectedRows.includes(startIndex + index) ? (
                        <MdCheckBox size={20} />
                      ) : (
                        <MdCheckBoxOutlineBlank size={20} />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {product.name}-{String(startIndex + index + 1).padStart(4, '0')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {unit.age}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      unit.needsRework 
                        ? 'bg-orange-100 text-orange-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {unit.needsRework ? 'Needs Rework' : 'Ready'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {unit.needsRework && (
                      <button 
                        onClick={() => handleScheduleRework([startIndex + index])}
                        className="text-orange-600 hover:text-orange-800 transform hover:scale-105 transition-all duration-200"
                      >
                        Schedule Rework
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination with 3D Effect */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">{endIndex}</span> of{' '}
                <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200"
                >
                  Previous
                </button>
                {getPageNumbers().map((pageNum, index) => (
                  pageNum === '...' ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transform hover:scale-105 transition-all duration-200 ${
                        currentPage === pageNum
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  )
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails; 